import AuxTabs from "@/components/AuxTabs";
import Recipe from "@/components/Recipe";
import { useAdContext } from "@/contexts/AdContext";
import { useHeaderContext } from "@/contexts/HeaderContext";
import { RecipeContextProvider } from "@/contexts/RecipeContext";
import useFaqs from "@/hooks/useFaqs";
import useSubscriptionPermission from "@/hooks/useSubscriptionPermission";
import apiClient from "@/lib/apiClient";
import craft from "@/lib/craft";
import getCommonStaticProps from "@/lib/getCommonStaticProps";
import QueryMealPlan from "@/queries/QueryMealPlan.graphql";
import QueryRecipe from "@/queries/QueryRecipe.graphql";
import AdRosTop from "@/src/ads/components/AdRosTop";
import { useMediaQuery } from "@/src/hooks/useMediaQuery";
import Notes from "@/src/notes/components/Notes";
import Reaction from "@/src/reactions/components/Reaction";
import RecipeHero from "@/src/recipes/components/RecipeHero";
import createBreadcrumbsJsonLd from "@/src/recipes/lib/createBreadcrumbsJsonLd";
import createRecipeFaqsJsonLd from "@/src/recipes/lib/createRecipeFaqsJsonLd";
import createRecipeJsonLd from "@/src/recipes/lib/createRecipeJsonLd";
import clsx from "clsx";
import { useEffect } from "react";

export async function getStaticPaths() {
  return { paths: [], fallback: "blocking" };
}

export async function getStaticProps({ params, previewData, preview }) {
  // Fetch common static properties
  const commonStaticProps = await getCommonStaticProps();

  const { slug, recipeSlug } = params;

  const queries = [
    {
      document: QueryMealPlan,
      variables: {
        slug,
      },
    },
    {
      document: QueryRecipe,
      variables: {
        slug: recipeSlug,
        status: ["live", "pending", "disabled"],
        isMealPlan: true,
      },
    },
  ];

  const response = await craft(queries, preview ? previewData.token : null);

  const { mealPlan } = response[0];
  const { recipe } = response[1];

  if (!recipe) {
    return {
      props: {
        ...commonStaticProps,
      },
      notFound: true,
    };
  }

  let notes = [];
  let totalNotesIncludingReplies = 0;

  try {
    const notesResponse = await apiClient.get(
      `/notes/${recipe.id}`,
      undefined,
      {},
      false
    );

    if (notesResponse && notesResponse.success) {
      notes = notesResponse.notes;
      totalNotesIncludingReplies =
        notesResponse.metadata?.totalIncludingReplies;
    }
  } catch (error) {
    console.error("Error fetching notes:", error);
  }

  let rating = {
    ratingCount: 0,
    ratingValue: 0,
    ratingPriorCount: 0,
  };

  try {
    const ratingResponse = await apiClient.get(
      `/ratings/${recipe.id}`,
      undefined,
      {},
      false
    );

    if (ratingResponse?.success) {
      const { totalReactions, finalRating, priorCount } = ratingResponse.rating;

      rating = {
        ratingCount: totalReactions,
        ratingValue: finalRating,
        ratingPriorCount: priorCount,
      };
    }
  } catch (error) {
    console.error("Error fetching rating:", error);
  }

  const { seomatic, ...recipeDetails } = recipe;

  // Use the new augmentRecipeJsonLd function
  const recipeJsonLdSchema = createRecipeJsonLd({
    recipe,
    rating,
    notes,
  });

  const breadcrumbs = [
    { label: "Meal Plans", href: "/meal-plans" },
    { label: mealPlan.title, href: `/meal-plans/${slug}` },
    { label: recipe.title, href: null },
  ];

  const jsonLdSchema = [
    recipeJsonLdSchema,
    createBreadcrumbsJsonLd(breadcrumbs),
  ];

  // Generate FAQs JSON-LD
  if (!!recipe.recipeFaqs && recipe.recipeFaqs.length > 0) {
    jsonLdSchema.push(createRecipeFaqsJsonLd(recipe.recipeFaqs));
  }
  return {
    props: {
      ...commonStaticProps,
      mealPlan,
      recipe,
      seomatic: recipe.seomatic,
      preview: preview ?? false,
      notes,
      totalNotesIncludingReplies,
      rating,
      jsonLdSchema,
      breadcrumbs,
    },
    revalidate: process.env.VERCEL_ENV !== "production" ? 1 : 3600,
  };
}

const MealPlanRecipeEntry = ({
  mealPlan,
  recipe,
  preview = false,
  globalFaqs,
  notes,
  totalNotesIncludingReplies,
  rating,
}) => {
  // Set showAds to true for this page
  const { setShowAds } = useAdContext();
  useEffect(() => setShowAds(true));

  const forceRestriction: boolean =
    process.env.NEXT_PUBLIC_PAYWALL_ALL_RECIPES === "true" ?? false;

  const { subscriptionPermission, contentIsRestricted } =
    useSubscriptionPermission(mealPlan, forceRestriction);

  // Determine whether the meal plan is restricted
  const redacted =
    !subscriptionPermission.canViewMobPlusMealPlans &&
    contentIsRestricted &&
    !preview;

  const isPremiumElement = parseInt(recipe.accessLevel) === 2;

  // Determine whether notes are enabled: for the time being, this is if the env setting is yes
  // and if the recipe is premium
  const isNotesEnabled =
    process.env.NEXT_PUBLIC_ENABLE_NOTES_ON_RECIPES === "yes" &&
    isPremiumElement;

  // We always need to make sure we have a rating
  const ratingValue = rating?.ratingValue;

  // Get the previous and next recipe in the meal plan according to which variant is seelcted (if any)
  const variant = mealPlan.planVariants?.[0];

  const mealPlanRecipes = !!variant ? variant?.recipes : mealPlan.recipes;

  const recipeIndex = mealPlanRecipes.findIndex(
    (planRecipe) => planRecipe.id === recipe.id
  );

  const prevRecipe = mealPlanRecipes[recipeIndex - 1] || null;
  const nextRecipe = mealPlanRecipes[recipeIndex + 1] || null;

  const isSmallScreen = useMediaQuery("(min-width: 480px)");

  // Show the header border for this page
  const { setShowBorder } = useHeaderContext();
  useEffect(() => {
    setShowBorder(true);
    return () => setShowBorder(false);
  }, []);

  return (
    <>
      <div className={clsx("Plan__hero", "relative bg-white print:hidden", {})}>
        <RecipeHero
          entry={recipe}
          mealPlan={mealPlan}
          ratingValue={ratingValue}
          totalNotesIncludingReplies={totalNotesIncludingReplies}
          nextRecipeHref={
            !!nextRecipe
              ? `/meal-plans/${mealPlan.slug}/${nextRecipe?.slug}`
              : null
          }
          prevRecipeHref={
            !!prevRecipe
              ? `/meal-plans/${mealPlan.slug}/${prevRecipe?.slug}`
              : null
          }
        />
      </div>

      <AdRosTop />

      <RecipeContextProvider>
        <Recipe recipe={recipe} redacted={redacted} globalFaqs={globalFaqs} />
      </RecipeContextProvider>

      <div id="reaction" className={clsx("border-b border-t py-6 lg:py-12")}>
        <div className={clsx("mx-auto max-w-screen-sm px-6 lg:px-12")}>
          <Reaction
            elementId={recipe.id}
            isNotesEnabled={isNotesEnabled}
            isPremiumElement={isPremiumElement}
          />
        </div>
      </div>

      {isNotesEnabled && (
        <div id="notes" className={clsx("border-b py-6 lg:py-12")}>
          <div className={clsx("mx-auto max-w-screen-sm px-6 lg:px-12")}>
            <Notes
              initialNotes={notes}
              elementId={parseInt(recipe.id)}
              chefId={
                recipe.chefs?.[0]?.id ? parseInt(recipe.chefs[0].id) : undefined
              }
              isPremiumElement={isPremiumElement}
            />
          </div>
        </div>
      )}

      {isSmallScreen && (
        <div className={clsx("block lg:hidden")}>
          <_AuxTabs recipe={recipe} globalFaqs={globalFaqs} />
        </div>
      )}
    </>
  );
};

const _AuxTabs = ({ recipe, globalFaqs }) => {
  const faqsToDisplay = useFaqs(
    globalFaqs.items,
    recipe.recipeFaqs,
    "recipes",
    false
  );

  return (
    <AuxTabs
      recipe={recipe}
      faqs={faqsToDisplay}
      faqIntro={globalFaqs.intro?.faqIntro}
    />
  );
};

export default MealPlanRecipeEntry;
